import { all, call, delay, getContext, put, select } from 'redux-saga/effects';
import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger';
import { getCheckServiceParams, getPublishTerms } from './utils';
import { trackBetChange, trackBetTyping } from './tracking';
import {
  CHECK_SERVICE_AFTER_CHANGE_BET,
  checkServiceFail,
  checkServiceStart,
  checkServiceSuccess,
} from '../../../actions/checkoutInfo';
import { SELECT_PUBLISH_TERMS_SERVICE, setPromotionTypeChangeReason } from '../../../actions/publishTerms';
import { changeAuctionBetSteps } from '../../../actions/auction';
import { changeCurrentBet } from '../../../thunks/auction/changeCurrentBet';
import { selectAnalyticsOfferStatus, selectAnalyticsProduct } from '../../../selectors/analytics';
import { selectCurrentBet, selectIsAuctionSelected, selectMinimalBet } from '../../../selectors/auction';
import { selectBetPosition } from '../../../selectors/betPosition';
import { selectLocationIds, selectOfferCoordinatesWithFallback, selectPhotos } from '../../../selectors/offer';
import {
  selectActivePackage,
  selectActivePlacingType,
  selectActiveService,
  selectIsIncreaseToMinimalTopBet,
  selectIsWithoutServices,
  selectPlacingMoneySource,
  selectPublicationPeriod,
  selectPublicationTokens,
} from '../../../selectors/publishTerms';
import { selectTermsDurationInDays } from '../../../selectors/termsDurations';
import { IAnalyticsProduct, TAnalyticsOfferStatus } from '../../../types/analytics';
import { IAppState, TActions, IAppEvent } from '../../../../AppModulesWrapper';
import { IBetPosition } from '../../../types/reducers/betPosition';
import { ITelemetryLogger } from '@cian/telemetry/shared';
import { checkService as checkServiceRequest, TCheckServiceResult } from '../../../services/checkService';
import { selectDynamicPricingData } from '../../../selectors/dynamicPricing';
import { selectModulePublishTermsResponse } from '../../../selectors/module';
import { ECheckServiceStatus } from '../../../types/reducers/checkoutInfo';
import { sendCheckServiceEvent } from './utils/sendCheckServiceEvent';
import { changeSelectedServiceAfterCheckService } from '../../../thunks/checkout/changeSelectedServiceAfterCheckService';
import { updateBalance, updateRecommendedDepositAmount } from '../../../actions/balance';

export function* checkService(checkServiceAction: TActions) {
  const httpApi: IHttpApi = yield getContext('httpApi');
  const logger: ILogger = yield getContext('logger');
  const telemetry: ITelemetryLogger = yield getContext('telemetry');
  const appEvent: IAppEvent = yield getContext('appEvent');
  let state: IAppState = yield select();

  const service = selectActiveService(state);
  const servicePackage = selectActivePackage(state);
  const publicationPeriod = selectPublicationPeriod(state);
  const isAuctionSelected = selectIsAuctionSelected(state);
  const placingType = selectActivePlacingType(state);
  const locationPath = selectLocationIds(state);
  const photos = selectPhotos(state);
  const coordinates = selectOfferCoordinatesWithFallback(state);
  const moneySource = selectPlacingMoneySource(state);
  const extraData = selectDynamicPricingData(state);
  const increaseToMinimalTopBet = selectIsIncreaseToMinimalTopBet(state);
  const isWithoutServices = selectIsWithoutServices(state);
  const minimalBet = selectMinimalBet(state);
  const termsDurationInDays = selectTermsDurationInDays(state);
  const publicationTokens = selectPublicationTokens(state);
  const currentBet = selectCurrentBet(state);

  const { offer, checkoutInfo, auction, calltracking } = state.servicePickerWidget;

  const isServiceChanged = checkServiceAction.type === SELECT_PUBLISH_TERMS_SERVICE;

  const terms = getPublishTerms({
    service,
    servicePackage,
    publicationPeriod,
    placingType,
    auction,
    isAuctionSelected,
    calltracking,
    increaseToMinimalTopBet,
    isWithoutServices,
    minimalBet,
    termsDurationInDays,
    currentBet,
  });

  yield put(checkServiceStart());
  appEvent.onCheckServiceStatusChange(ECheckServiceStatus.pending);

  const parameters = getCheckServiceParams({
    offer,
    locationPath,
    checkoutInfo,
    terms,
    isServiceChanged,
    photosCount: photos.length,
    coordinates,
    moneySource,
    extraData,
    publicationTokens,
  });

  const [response]: [TCheckServiceResult] = yield all([
    call(checkServiceRequest, {
      httpApi,
      logger,
      telemetry,
      parameters,
    }),
    // При быстром ответе сервера лоадер появляется на долю секунд в инпуте ставки, из-за чего происходит мерцание.
    // Для хорошего UX ограничиваем минимально возможное выполнение запроса
    delay(1000),
  ]);

  if (response.success) {
    const { selectedPromotionInfo, auction, balance, recommendedDepositAmount } = response;

    appEvent.onCheckServiceStatusChange(ECheckServiceStatus.success);

    yield put(updateRecommendedDepositAmount(recommendedDepositAmount));

    yield put(
      changeSelectedServiceAfterCheckService({
        publicationType: selectedPromotionInfo.publicationType,
        promotionType: selectedPromotionInfo.promotionType,
        serviceId: selectedPromotionInfo.serviceId,
      }),
    );

    if (auction) {
      yield put(changeAuctionBetSteps({ previousBets: auction.previousBets, nextBets: auction.nextBets }));
      yield put(changeCurrentBet({ afterCheckService: true, bet: auction.currentBet }));
    }

    yield put(setPromotionTypeChangeReason(selectedPromotionInfo.promotionTypeChangeReason));

    yield put(checkServiceSuccess(response.payload));

    yield put(updateBalance(balance));

    if (checkServiceAction.type === CHECK_SERVICE_AFTER_CHANGE_BET) {
      const analyticsOfferStatus: TAnalyticsOfferStatus = yield select(selectAnalyticsOfferStatus);
      const analyticsProduct: IAnalyticsProduct = yield select(selectAnalyticsProduct);
      const betPosition: IBetPosition | null = yield select(selectBetPosition);

      const { currentBet } = auction;
      const { offerPosition = null, page = null } = betPosition || {};

      if (checkServiceAction.payload === 'input') {
        trackBetTyping({
          analyticsOfferStatus,
          analyticsProduct,
          selectedServiceId: selectedPromotionInfo.serviceId,
          currentBet,
          offerPosition,
          page,
        });
      } else {
        // Трекаем событие клика по кнопкам поля ввода ставки
        trackBetChange({
          analyticsOfferStatus,
          analyticsProduct,
          sign: checkServiceAction.payload,
          selectedServiceId: selectedPromotionInfo.serviceId,
          currentBet,
          offerPosition,
          page,
        });
      }
    }
  } else {
    yield put(checkServiceFail(response.payload));
    appEvent.onCheckServiceStatusChange(ECheckServiceStatus.fail);
  }

  state = yield select();
  sendCheckServiceEvent(state, response);

  const newState: IAppState = yield select();
  const eventTerms = selectModulePublishTermsResponse(newState);
  appEvent.onCheckServiceChange(eventTerms);
}
